import { AxiosError } from "axios";
import { ShortcutGroupToShortcuts, ShortcutUpdateModelv1, ShortcutsFilterv1, ShortcutsState, defaultShortcutsFilter } from "./models";
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected, PayloadAction } from "@reduxjs/toolkit";
import { shortcutsApi } from "api/instances";

const defaultState: ShortcutsState = {
    filteredShortcutFormat: [],
    originalShortcutFormat: [],
    shortcutFeed: [],
    isLoadingOriginalShortcutFormat: false,
    isFetching: false,
    isUpdating: false,
    successMessage: "",
    errorMessage: "",
    scrollToRowId: undefined,
};

interface ISetMsgAction {
    payload: string | undefined;
}

interface ISetScrollToRowIdAction {
    payload: string | undefined;
}

const handleError = (err: AxiosError, thunkApi) => {
    if (!err.isAxiosError) throw err;

    if (err.response?.status !== 200) {
        thunkApi.dispatch({ type: SET_ERR_MSG , payload: "There was an error. Please try again later." });
    }
}

const handleSuccess = (thunkApi, successMsg?: string) => {
    thunkApi.dispatch(SET_SUCCESS_MSG(successMsg));
}

export const getShortcutFormat = createAsyncThunk(
    "GET_SHORTCUT_FORMAT",
    async (filter?: ShortcutsFilterv1) => {
        try {
            return await shortcutsApi.getShortcutsFormat(filter ?? defaultShortcutsFilter);
        }
        catch (err) {
            throw err;
        }
    }
);

export const getOriginalShortcutFormat = createAsyncThunk(
    "GET_ORIGINAL_SHORTCUT_FORMAT",
    async _ => {
        try {
            return await shortcutsApi.getShortcutsFormat(defaultShortcutsFilter);
        }
        catch(err) {
            throw err;
        }
    }
);

export const getShortcutGroups = createAsyncThunk(
    "GET_SHORTCUT_GROUPS",
    async (searchText: string) => {
        try {
            await shortcutsApi.getShortcutGroups(searchText);
        }
        catch(err) {
            throw err;
        }
    }
)

export const createShortcut = createAsyncThunk(
    "CREATE_SHORTCUT", 
    async (payload: {shortcutUpdateModel: ShortcutUpdateModelv1, filter: ShortcutsFilterv1, scrollToId?: string, successMsg?: string}, thunkApi) => {
        await shortcutsApi.createNewShortcut(payload.shortcutUpdateModel)
            .then(() => handleSuccess(thunkApi, payload.successMsg))
            .catch((err: AxiosError<any>) => {
                handleError(err, thunkApi);
                throw err;
            });

        thunkApi.dispatch(getShortcutFormat(payload.filter));
        thunkApi.dispatch(getOriginalShortcutFormat());
        thunkApi.dispatch(SET_SCROLL_TO_ROW_ID(payload.scrollToId));
    }
);

export const createShortcutGroup = createAsyncThunk(
    "CREATE_SHORTCUT_GROUP", 
    async (payload: {shortcutGroupName: string, filter: ShortcutsFilterv1, scrollToId?: string, successMsg?: string}, thunkApi) => {
        await shortcutsApi.createNewShortcutGroup(payload.shortcutGroupName)
            .catch((err: AxiosError<any>) => {
                handleError(err, thunkApi);
                throw err;
            });

        handleSuccess(thunkApi, payload.successMsg);

        thunkApi.dispatch(getShortcutFormat(payload.filter));
        thunkApi.dispatch(getOriginalShortcutFormat());
        thunkApi.dispatch(SET_SCROLL_TO_ROW_ID(payload.scrollToId));
    }
);

interface IUpdateShortcutPayload {
    shortcutId: string;
    shortcutUpdateModel: ShortcutUpdateModelv1;
    filter: ShortcutsFilterv1;
    successMsg?: string;
}

export const updateShortcut = createAsyncThunk(
    "UPDATE_SHORTCUT", 
    async ({ shortcutId, shortcutUpdateModel, filter, successMsg }: IUpdateShortcutPayload, thunkApi) => {
        await shortcutsApi.updateShortcut(shortcutId, shortcutUpdateModel)
            .then(() => handleSuccess(thunkApi, successMsg))
            .catch((err: AxiosError<any>) => {
                handleError(err, thunkApi);
                throw err;
            });
        
        thunkApi.dispatch(getShortcutFormat(filter));
    }
);

interface IUpdateShortcutGroupPayload {
    shortcutGroupId: string;
    name: string;
    filter: ShortcutsFilterv1,
    successMsg?: string;
}

export const updateShortcutGroup = createAsyncThunk(
    "UPDATE_SHORTCUT_GROUP", 
    async ({ shortcutGroupId, name, filter, successMsg }: IUpdateShortcutGroupPayload, thunkApi) => {
        await shortcutsApi.updateShortcutGroupName(shortcutGroupId, name)
            .catch((err: AxiosError<any>) => {
                handleError(err, thunkApi);
                throw err;
            });

        handleSuccess(thunkApi, successMsg);

        thunkApi.dispatch(getShortcutFormat(filter));
    }
);

export const bulkDeleteShortcutsAndGroups = createAsyncThunk(
    "DELETE_SHORTCUTS_AND_GROUPS", 
    async (payload: {shortcutIds: string[], shortcutGroupIds: string[], filter: ShortcutsFilterv1, successMsg?: string}, thunkApi) => {
        if (payload.shortcutIds.length)
            await shortcutsApi.bulkDeleteShortcuts(payload.shortcutIds)
                .catch((err: AxiosError<any>) => {
                    handleError(err, thunkApi);
                    throw err;
                });

        if (payload.shortcutGroupIds.length)
            await shortcutsApi.bulkDeleteShortcutGroups(payload.shortcutGroupIds)
                .catch((err: AxiosError<any>) => {
                    handleError(err, thunkApi);
                    throw err;
                });

        handleSuccess(thunkApi, payload.successMsg);
        thunkApi.dispatch(getShortcutFormat(payload.filter));
        thunkApi.dispatch(getOriginalShortcutFormat());
    }
);

export const bulkDeleteShortcuts = createAsyncThunk(
    "DELETE_SHORTCUTS", 
    async (payload: {shortcutIds: string[], filter: ShortcutsFilterv1}, thunkApi) => {
        await shortcutsApi.bulkDeleteShortcuts(payload.shortcutIds)
            .catch((err: AxiosError<any>) => {
                handleError(err, thunkApi);
                throw err;
            });

        thunkApi.dispatch(getShortcutFormat(payload.filter));
        thunkApi.dispatch(getOriginalShortcutFormat());
    }
);

export const bulkDeleteShortcutGroups = createAsyncThunk(
    "DELETE_SHORTCUT_GROUPS", 
    async (payload: {shortcutGroupIds: string[], filter: ShortcutsFilterv1}, thunkApi) => {
        await shortcutsApi.bulkDeleteShortcutGroups(payload.shortcutGroupIds)
            .catch((err: AxiosError<any>) => {
                handleError(err, thunkApi);
                throw err;
            });

        thunkApi.dispatch(getShortcutFormat(payload.filter));
        thunkApi.dispatch(getOriginalShortcutFormat());
    }
);

interface IBulkUpdateShortcutsPayload {
    shortcutIds: string[];
    setIsEnabled?: boolean;
    groupIdToMoveTo?: string;
    filter: ShortcutsFilterv1;
    scrollToId?: string;
    successMsg?: string;
}

export const bulkUpdateShortcuts = createAsyncThunk(
    "BULK_UPDATE_SHORTCUTS",
    async ({shortcutIds, setIsEnabled, groupIdToMoveTo, filter, scrollToId, successMsg}: IBulkUpdateShortcutsPayload, thunkApi) => {
        await shortcutsApi.bulkUpdateShortcuts(shortcutIds, setIsEnabled, groupIdToMoveTo)
            .then(() => handleSuccess(thunkApi, successMsg))
            .catch((err: AxiosError<any>) => {
                handleError(err, thunkApi);
                throw err;
            });

        thunkApi.dispatch(getShortcutFormat(filter));

        thunkApi.dispatch(SET_SCROLL_TO_ROW_ID(scrollToId));
    }
)

export const updateShortcutsFormat = createAsyncThunk(
    "UPDATE_SHORTCUTS_FORMAT",
    async (payload: {newFormat: ShortcutGroupToShortcuts[], filter: ShortcutsFilterv1, successMsg?: string}, thunkApi) => {
        await shortcutsApi.updateShortcutsFormat(payload.newFormat)
            .then(() => handleSuccess(thunkApi, payload.successMsg))
            .catch((err: AxiosError<any>) => {
                handleError(err, thunkApi);
                throw err;
            });

        thunkApi.dispatch(getShortcutFormat(payload.filter));
        thunkApi.dispatch(getOriginalShortcutFormat());
    }
)

export const getShortcutsFeed = createAsyncThunk(
    "GET_SHORTCUTS_FEED_LIST",
    async _ => {
        try {
            return await shortcutsApi.getClientShortcutsList();
        }
        catch(err) {
            throw err;
        }
    }
)

const pendingUpdateMatcher = isPending(
    updateShortcut,
    updateShortcutGroup,
    bulkUpdateShortcuts,
    updateShortcutsFormat,
    bulkDeleteShortcutsAndGroups
);

const fulfilledUpdateMatcher = isFulfilled(
    updateShortcut,
    updateShortcutGroup,
    bulkUpdateShortcuts,
    updateShortcutsFormat,
    bulkDeleteShortcutsAndGroups
);

const rejectedUpdateMatcher = isRejected(
    updateShortcut,
    updateShortcutGroup,
    bulkUpdateShortcuts,
    updateShortcutsFormat,
    bulkDeleteShortcutsAndGroups
);

const pendingFetchMatcher = isPending(
    getShortcutFormat,
    getShortcutsFeed
)

const rejectedFetchMatcher = isRejected(
    getShortcutFormat,
    getShortcutsFeed
)

export const shortcutsSlice = createSlice({
    name: "shortcuts",
    initialState: defaultState,
    reducers: {
        SET_ERR_MSG: (state: ShortcutsState, action: ISetMsgAction) => {
            state.errorMessage = action.payload;
        },
        SET_SUCCESS_MSG: (state: ShortcutsState, action: ISetMsgAction) => {
            state.successMessage = action.payload;
        },
        SET_SCROLL_TO_ROW_ID: (state: ShortcutsState, action: ISetScrollToRowIdAction) => {
            state.scrollToRowId = action.payload;
        }
    },
    extraReducers: (builder) => builder
        .addCase(getShortcutFormat.fulfilled, (state, action: PayloadAction<ShortcutGroupToShortcuts[]>) => { 
            state.isFetching = false;
            state.filteredShortcutFormat = action.payload;
        })
        .addCase(getShortcutsFeed.fulfilled, (state, action: PayloadAction<ShortcutGroupToShortcuts[]>) => { 
            state.isFetching = false;
            state.shortcutFeed = action.payload;
        })
        .addCase(getOriginalShortcutFormat.fulfilled, (state, action: PayloadAction<ShortcutGroupToShortcuts[]>) => {
            state.isLoadingOriginalShortcutFormat = false;
            state.originalShortcutFormat = action.payload;
        })
        .addCase(getOriginalShortcutFormat.pending, state => { state.isLoadingOriginalShortcutFormat = true })
        .addCase(getOriginalShortcutFormat.rejected, state => { state.isLoadingOriginalShortcutFormat = false })
        .addMatcher(pendingFetchMatcher, state => { state.isFetching = true })
        .addMatcher(rejectedFetchMatcher, state => { state.isFetching = false })
        .addMatcher(pendingUpdateMatcher, state => { state.isUpdating = true })
        .addMatcher(fulfilledUpdateMatcher, state => {
            state.isUpdating = false;
            if (state.errorMessage !== "") state.errorMessage = "";
        })
        .addMatcher(rejectedUpdateMatcher, state => {
            state.isUpdating = false;
            if (state.successMessage !== "") state.successMessage = "";
        })
});

export const { SET_SCROLL_TO_ROW_ID, SET_ERR_MSG, SET_SUCCESS_MSG } = shortcutsSlice.actions;